import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomButton from '../../../components/CustomButton';
import { useReplaceFeaturedImageMutation, useUploadAssetImagesMutation } from '../../../api/asset/assetApiSlice';
import { useAddOwnTermsDocumentMutation, useUpdateOwnTermsDocumentMutation } from '../../../api/account/accountApiSlice';

/** One attachment the page uploads after the import has committed, through its own existing route. */
export type UploadTask =
    | { id: string, kind: 'photo', room: string, assetId: string, file: File, featured: boolean }
    | { id: string, kind: 'terms', title: string, documentId: string | null, file: File };

type UploadState = { status: 'waiting' | 'uploading' | 'done' } | { status: 'failed', detail: string };

/** The server's message behind a failed upload, or the cause when there is no answer. */
const failureDetail = (err: unknown, t: (key: string) => string): string => {
    const e = err as { status?: number, data?: { message?: unknown } } | undefined;
    if (e?.status === undefined) return t('onboarding_import.upload.no_answer');
    return typeof e.data?.message === 'string' ? e.data.message : t('onboarding_import.upload.failed_generic');
};

/**
 * The photos and the Nutzungsordnung, uploaded one at a time after
 * "Übernehmen" succeeded: a room's first photo as its cover
 * (`PUT /asset/:id/featured-image`), the others as its images
 * (`POST /asset/:id/images`), the PDF as an own-terms document. Each item shows
 * its state; a failed one can be retried. The import itself is already saved —
 * a failed upload never undoes it. `slug`: a master on the tenant in view, whose
 * PDF goes through the by-slug own-terms route (PO, 2026-10-30).
 */
const AttachmentUploads: React.FC<{ tasks: UploadTask[], slug?: string }> = ({ tasks, slug }) => {
    const { t } = useTranslation();
    const [replaceFeatured] = useReplaceFeaturedImageMutation();
    const [uploadImages] = useUploadAssetImagesMutation();
    const [addTerms] = useAddOwnTermsDocumentMutation();
    const [updateTerms] = useUpdateOwnTermsDocumentMutation();
    const [states, setStates] = useState<Record<string, UploadState>>(
        () => Object.fromEntries(tasks.map((task) => [task.id, { status: 'waiting' }])),
    );
    const started = useRef(false);

    const send = useCallback(async (task: UploadTask) => {
        if (task.kind === 'terms') {
            if (task.documentId) await updateTerms({ id: task.documentId, file: task.file, slug }).unwrap();
            else await addTerms({ title: task.title, file: task.file, slug }).unwrap();
        } else if (task.featured) {
            await replaceFeatured({ assetId: task.assetId, file: task.file }).unwrap();
        } else {
            await uploadImages({ assetId: task.assetId, files: [task.file] }).unwrap();
        }
    }, [addTerms, replaceFeatured, slug, updateTerms, uploadImages]);

    const run = useCallback(async (task: UploadTask) => {
        setStates((prev) => ({ ...prev, [task.id]: { status: 'uploading' } }));
        try {
            await send(task);
            setStates((prev) => ({ ...prev, [task.id]: { status: 'done' } }));
        } catch (err) {
            setStates((prev) => ({ ...prev, [task.id]: { status: 'failed', detail: failureDetail(err, t) } }));
        }
    }, [send, t]);

    // One pass, one at a time, when the list arrives. Retries run per item.
    useEffect(() => {
        if (started.current) return;
        started.current = true;
        (async () => {
            for (const task of tasks) await run(task);
        })();
    }, [tasks, run]);

    if (tasks.length === 0) return null;
    const done = tasks.filter((task) => states[task.id]?.status === 'done').length;

    return (
        <div className={'flex flex-col gap-2'}>
            <p className={'text-sm font-medium text-foreground'}>
                {t('onboarding_import.upload.title', { done, total: tasks.length })}
            </p>
            <ul className={'flex flex-col gap-2 text-sm'}>
                {tasks.map((task) => {
                    const state = states[task.id] ?? { status: 'waiting' };
                    const label = task.kind === 'terms'
                        ? t('onboarding_import.upload.terms', { title: task.title, file: task.file.name })
                        : t('onboarding_import.upload.photo', { room: task.room, file: task.file.name });
                    return (
                        <li key={task.id} className={'flex flex-wrap items-center gap-3'}>
                            <span className={'text-foreground'}>{label}</span>
                            <span className={state.status === 'failed' ? 'font-medium text-foreground' : 'text-foreground/60'}>
                                {state.status === 'failed'
                                    ? t('onboarding_import.upload.failed', { detail: state.detail })
                                    : t(`onboarding_import.upload.${state.status}`)}
                            </span>
                            {state.status === 'failed' && (
                                <CustomButton title={t('onboarding_import.upload.retry')} isFilled={false} onClick={() => { run(task); }} />
                            )}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default AttachmentUploads;
